import { Button, FormControl, FormLabel, Input, Stack, useDisclosure } from '@elkaso-app/web-design';
import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { usePageParams } from 'hooks/use-page-params';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

type TInputs = {
  country?: string;
};

export const CountryFilterButton = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<TInputs>();

  // Get current filter values from URL
  const { country } = getPageParams();

  // Set initial form values
  useEffect(() => {
    if (isOpen) {
      reset({
        country: country ? String(country) : '',
      });
    }
  }, [isOpen, country, reset]);

  const onSubmit = useCallback(
    (data: TInputs) => {
      const currentParams = getPageParams();
      const newParams = { ...currentParams };

      if (data.country?.trim()) {
        newParams.country = data.country.trim();
      } else {
        delete newParams.country;
      }

      setPageParams(newParams);
      onClose();
    },
    [setPageParams, getPageParams, onClose]
  );

  const onClear = useCallback(() => {
    const currentParams = getPageParams();
    const newParams = { ...currentParams };

    delete newParams.country;

    setPageParams(newParams);
    reset();
    onClose();
  }, [getPageParams, setPageParams, reset, onClose]);

  // Check if any filters are active
  const hasFilters = !!country;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Prevent form submission from closing the modal
  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit(onSubmit)(e);
    },
    [handleSubmit, onSubmit]
  );

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={hasFilters}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleFormSubmit} onClick={(e) => e.stopPropagation()}>
          <FormControl>
            <FormLabel>Country</FormLabel>
            <Input size='sm' placeholder='Search by country...' {...register('country')} />
          </FormControl>

          <Stack direction='row' justify='flex-end' spacing={2}>
            <Button variant='outline' colorScheme='red' onClick={onClear}>
              Clear
            </Button>
            <Button variant='outline' colorScheme='gray' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='blue' type='submit' isLoading={isSubmitting}>
              Filter
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};
